import { API_BASE } from '../config';
import { getAuthToken } from '../utils/authStorage';

async function authHeaders() {
  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// CHEMICALS
export async function fetchChemicals({ search = null, skip = 0, limit = 100, filterType = 'all' } = {}) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (skip) params.set('skip', String(skip));
  if (limit) params.set('limit', String(limit));
  if (filterType && filterType !== 'all') params.set('filter_type', filterType);

  const qs = params.toString();
  const url = `${API_BASE}/chemicals/${qs ? `?${qs}` : ''}`;

  const res = await fetch(url, { 
    headers: await authHeaders() 
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch chemicals');
  }
  return res.json();
}

export async function fetchChemical(id) {
  const res = await fetch(`${API_BASE}/chemicals/${id}`, { 
    headers: await authHeaders() 
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch chemical');
  }
  return res.json();
}

export async function createChemical(data) {
  const res = await fetch(`${API_BASE}/chemicals/`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to create chemical');
  }
  return res.json();
}

export async function updateChemical(id, data) {
  const res = await fetch(`${API_BASE}/chemicals/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to update chemical');
  }
  return res.json();
}

export async function addChemicalNote(id, note) {
  const res = await fetch(`${API_BASE}/chemicals/${id}/notes`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ note }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to add note');
  }
  return res.json();
}

export async function deleteChemical(id) {
  const res = await fetch(`${API_BASE}/chemicals/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to delete chemical');
  }
  return res.json();
}

export async function updateChemicalStock(chemicalId, quantityChange) {
  const res = await fetch(`${API_BASE}/chemicals/${chemicalId}/stock/update?quantity_change=${quantityChange}`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to update chemical stock');
  }
  return res.json();
}

export async function updateChemicalPurchasePrice(chemicalId, purchasePrice) {
  const res = await fetch(`${API_BASE}/chemicals/${chemicalId}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ purchase_price: purchasePrice }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to update purchase price');
  }
  return res.json();
}

export async function checkAlerts() {
  const res = await fetch(`${API_BASE}/chemicals/check-alerts`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to check alerts');
  }
  return res.json();
}

// FORMULATIONS
export async function fetchFormulations(chemicalId = null) {
  const url = chemicalId 
    ? `${API_BASE}/formulations/chemical/${chemicalId}`
    : `${API_BASE}/formulations/`;
  const res = await fetch(url, { 
    headers: await authHeaders() 
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch formulations');
  }
  return res.json();
}

export async function fetchFormulation(id) {
  const res = await fetch(`${API_BASE}/formulations/${id}`, { 
    headers: await authHeaders() 
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to fetch formulation');
  }
  return res.json();
}

export async function createFormulation(data) {
  const res = await fetch(`${API_BASE}/formulations/`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to create formulation');
  }
  return res.json();
}

export async function updateFormulation(id, data) {
  const res = await fetch(`${API_BASE}/formulations/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to update formulation');
  }
  return res.json();
}

export async function addFormulationNote(id, note) {
  const res = await fetch(`${API_BASE}/formulations/${id}/notes`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ note }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to add note');
  }
  return res.json();
}

export async function deleteFormulation(id) {
  const res = await fetch(`${API_BASE}/formulations/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to delete formulation');
  }
  return res.json();
} 